import {
  dark,
  fontFamily,
  fontSize,
  fontWeight,
  light,
  lineHeight,
  motion,
  radius,
  space,
  type ModeColors,
  type Tone,
} from './palette';

const TONES: readonly Tone[] = ['success', 'warning', 'danger', 'info', 'neutral'];

const colorVars = (c: ModeColors): string =>
  [
    `--ob-bg: ${c.bg};`,
    `--ob-surface: ${c.surface};`,
    `--ob-surface-subtle: ${c.surfaceSubtle};`,
    `--ob-border: ${c.border};`,
    `--ob-border-subtle: ${c.borderSubtle};`,
    `--ob-text: ${c.text};`,
    `--ob-text-muted: ${c.textMuted};`,
    `--ob-text-subtle: ${c.textSubtle};`,
    `--ob-primary: ${c.primary};`,
    `--ob-primary-hover: ${c.primaryHover};`,
    `--ob-primary-active: ${c.primaryActive};`,
    `--ob-on-primary: ${c.onPrimary};`,
    `--ob-link: ${c.link};`,
    `--ob-link-hover: ${c.linkHover};`,
    `--ob-focus: ${c.focus};`,
    ...TONES.flatMap((tone) => [
      `--ob-${tone}: ${c.tone[tone]};`,
      `--ob-${tone}-text: ${c.toneText[tone]};`,
      `--ob-${tone}-bg: ${c.toneBg[tone]};`,
    ]),
    `--ob-shadow-sm: ${c.shadow.sm};`,
    `--ob-shadow-md: ${c.shadow.md};`,
    `--ob-shadow-lg: ${c.shadow.lg};`,
    `color-scheme: ${c === dark ? 'dark' : 'light'};`,
  ].join('');

const scaleVars = (): string =>
  [
    `--ob-font: ${fontFamily};`,
    ...Object.entries(fontSize).map(([k, v]) => `--ob-text-${k}: ${v}px;`),
    ...Object.entries(lineHeight).map(([k, v]) => `--ob-leading-${k}: ${v};`),
    ...Object.entries(fontWeight).map(([k, v]) => `--ob-weight-${k}: ${v};`),
    ...Object.entries(space).map(([k, v]) => `--ob-space-${String(k).replace('.', '_')}: ${v}px;`),
    ...Object.entries(radius).map(([k, v]) => `--ob-radius-${k}: ${v}px;`),
    `--ob-duration-fast: ${motion.fast};`,
    `--ob-duration: ${motion.base};`,
    `--ob-easing: ${motion.easing};`,
  ].join('');

/**
 * The tokens as CSS variables. Light by default; dark when the OS asks for it, unless the app's
 * theme switch has picked one (it sets `data-theme` on <html>, see ThemeModeContext).
 */
export const buildTokenCss = (): string =>
  `:root{${scaleVars()}${colorVars(light)}}` +
  `@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){${colorVars(dark)}}}` +
  `:root[data-theme="dark"]{${colorVars(dark)}}`;

const STYLE_ID = 'ob-tokens';

/** Puts the variables on the page once, before the first render, so nothing paints unthemed. */
export const injectTokenStyles = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = buildTokenCss();
  document.head.appendChild(style);
};
